import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Home, BookOpen, Gamepad2, Sparkles, Trophy, PenTool, Clapperboard } from "lucide-react";
import { Toaster } from "sonner";
import "@/App.css";

// Import pages
import HomePage from "./pages/HomePage";
import DiaryPage from "./pages/DiaryPage";
import GamesHubPage from "./pages/GamesHubPage";
import GamePlayPage from "./pages/GamePlayPage";
// Legacy in-app mini games (kept for now; not linked in nav)
import GamesPage from "./pages/GamesPage";
import CraftsPage from "./pages/CraftsPage";
import TreasurePage from "./pages/TreasurePage";
import MediaPage from "./pages/MediaPage";

import ErrorBoundary from "./components/ErrorBoundary";

// Bottom Navigation Component
const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: "/", icon: Home, label: "Home", color: "text-primary" },
    { path: "/media", icon: Clapperboard, label: "Media", color: "text-accent" },
    { path: "/diary", icon: BookOpen, label: "Diary", color: "text-magic" },
    { path: "/games", icon: Gamepad2, label: "Games", color: "text-secondary" },
    { path: "/crafts", icon: PenTool, label: "Crafts", color: "text-grass" },
    { path: "/treasure", icon: Trophy, label: "Treasure", color: "text-primary" }
  ];

  return (
    <nav
      data-testid="bottom-navigation"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md border border-white/50 shadow-2xl rounded-full p-2 flex gap-1 z-50 max-w-[95vw] overflow-x-auto"
    >
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;

        return (
          <button
            key={item.path}
            data-testid={`nav-${item.label.toLowerCase()}`}
            onClick={() => navigate(item.path)}
            className={`relative flex flex-col items-center justify-center p-2 md:p-3 rounded-full transition-all min-w-[60px] md:min-w-[70px] ${isActive
              ? `${item.color} bg-muted scale-110`
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
          >
            <Icon size={20} strokeWidth={2.5} className="md:w-6 md:h-6" />
            <span className="text-[10px] md:text-xs font-nunito font-semibold mt-1">{item.label}</span>
            {isActive && (
              <motion.div
                layoutId="activeNav"
                className="absolute inset-0 bg-muted rounded-full -z-10"
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
};

// Main App Component
const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate initial load
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <Sparkles className="w-16 h-16 text-primary mx-auto mb-4 animate-bounce" />
          <h1 className="font-fredoka text-4xl text-foreground mb-2">SparkleWorld</h1>
          <p className="font-nunito text-muted-foreground">Loading magic...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App min-h-screen bg-background font-nunito pb-28">
        <Toaster position="top-center" richColors />

        <ErrorBoundary>
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/media" element={<MediaPage />} />

              <Route path="/diary" element={<DiaryPage />} />
              <Route path="/games" element={<GamesHubPage />} />
              <Route path="/games/:slug" element={<GamePlayPage />} />
              <Route path="/arcade" element={<GamesPage />} />
              <Route path="/crafts" element={<CraftsPage />} />
              <Route path="/treasure" element={<TreasurePage />} />
            </Routes>
          </AnimatePresence>
        </ErrorBoundary>

        <BottomNav />
      </div>
    </BrowserRouter>
  );
};

export default App;
